import React, { useMemo } from 'react'
import { Stack } from '@mui/material'
import { useParams } from 'react-router-dom'

import ScrollTable from 'components/global/CUK/ScrollTable'
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton'
import { ResponsiveTableProvider } from 'packages/ui/Table'
import TableView, { useTable } from 'packages/ui/Table'
import { useNavigate } from 'router'
import { useSidebar } from 'components/AuthenticatedLayout/Sidebar/SidebarContext'
import { useResponsiveColumns } from 'packages/ui/Table'

import CONFIG_COLUMNS_STANDARD, { ExpandedRowDetails as StandardExpandedRowDetails } from './config-columns-standard'
import CONFIG_COLUMNS_H3, { ExpandedRowDetails as H3ExpandedRowDetails } from './config-columns-h3'
import { useSort, useCouponsSelected } from '../store'
import { trackEvent } from 'analytics'

const RESPONSIVE_COLUMNS_CONFIG = {
  mobile: ['availability', 'codes', 'sites', 'actions'],
  tablet: ['availability', 'codes', 'sites', 'offers', 'connexions', 'validity', 'actions'],
  desktop: ['check', 'availability', 'codes', 'sites', 'offers', 'connexions', 'validity', 'description', 'creation_date', 'actions'],
  desktopLarge: ['check', 'availability', 'codes', 'sites', 'offers', 'connexions', 'validity', 'phones', 'description', 'creation_date', 'created_by', 'actions']
}

const RESPONSIVE_COLUMNS_CONFIG_WITH_CARD = {
  mobile: ['availability', 'codes', 'sites', 'actions'],
  tablet: ['availability', 'codes', 'sites', 'actions'],
  desktop: ['check', 'availability', 'codes', 'sites', 'offers'],
  desktopLarge: ['check', 'availability', 'codes', 'sites', 'offers', 'validity', 'actions']
}

const Table = ({ apiSearch, isStandard }) => {
  const { id } = useParams()
  const [sort, setSort] = useSort()
  const [couponsSelected] = useCouponsSelected()
  const navigate = useNavigate()
  const { expanded: sidebarExpanded } = useSidebar()
  
  const columnsConfig = id ? RESPONSIVE_COLUMNS_CONFIG_WITH_CARD : RESPONSIVE_COLUMNS_CONFIG
  
  const { visibleColumns, getVisibleColumns } = useResponsiveColumns(
    columnsConfig,
    {
      sidebarExpanded,
      excludeColumns: []
    }
  )
  
  const columns = useMemo(() => {
    const baseColumns = isStandard ? CONFIG_COLUMNS_STANDARD : CONFIG_COLUMNS_H3
    return baseColumns.filter(({ name }) => visibleColumns.includes(name))
  }, [isStandard, visibleColumns])

  const responsiveConfig = useMemo(() => ({
    expandedRowRenderer: {
      component: isStandard ? StandardExpandedRowDetails : H3ExpandedRowDetails,
      shouldRender: (screenSize) => screenSize === 'mobile',
      getVisibleColumns
    }
  }), [isStandard, getVisibleColumns])

  const apiTable = useTable({
    rowKey: 'id',
    loading: apiSearch.loading,
    loadingNext: apiSearch.loadingNext,
    items: apiSearch.data ? apiSearch.data.coupons : [],
    columns,
    onRowClick: coupon => {
      trackEvent('code_details', { code_type: isStandard ? 'standard' : 'h3' })
      navigate({ pathname: `${coupon.id}` })
    },
    responsiveConfig
  })

  return (
    <ResponsiveTableProvider>
      <Stack
        sx={{
          overflow: 'hidden',
          overflowY: 'auto',
          flex: '1 1 auto',
          scrollbarWidth: 'none',
          height: '100%',
          '&::-webkit-scrollbar ': {
            display: 'none'
          }
        }}
      >
        <ScrollTable
          fetchNextPage={apiSearch.fetchNextPage}
          shouldFetchNextPage={apiSearch.shouldFetchNextPage}
          collapse={id || couponsSelected.length > 0}
        >
          {apiSearch.loading ? (
            <TableSkeleton />
          ) : (
            <TableView
              apiTable={apiTable}
              sort={{
                ...sort,
                onChange: name => {
                  const column = columns.find(column => column.name === name)
                  setSort(column.sortBy || column.name)
                }
              }}
            />
          )}
        </ScrollTable>
      </Stack>
    </ResponsiveTableProvider>
  )
}

export default Table
